import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Users, FileText, CheckSquare, Trash2, Shield, TrendingUp, UserPlus, Calendar } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/context/AuthContext';

const BACKEND_URL = window.location.origin;
const API = `${BACKEND_URL}/api`;

const AdminPage = () => {
  const navigate = useNavigate();
  const { token } = useAuth();
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!token) { navigate('/auth'); return; }
    fetchData();
  }, [token]);

  const fetchData = async () => {
    try {
      const [usersRes, statsRes] = await Promise.all([
        axios.get(`${API}/admin/users`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/stats`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setUsers(usersRes.data.users);
      setStats(statsRes.data);
      setError(null);
    } catch (err) {
      if (err.response?.status === 403) {
        setError('Accès réservé aux administrateurs');
      } else {
        setError('Erreur de chargement');
      }
    } finally {
      setLoading(false);
    }
  };

  const deleteUser = async (userId, userName) => {
    if (!window.confirm(`Supprimer l'utilisateur "${userName}" ? Cette action est irréversible.`)) return;
    try {
      await axios.delete(`${API}/admin/users/${userId}`, { headers: { Authorization: `Bearer ${token}` } });
      setUsers(users.filter(u => u.id !== userId));
      setStats(prev => prev ? { ...prev, total_users: prev.total_users - 1 } : prev);
    } catch (err) {
      alert(err.response?.data?.detail || 'Erreur lors de la suppression');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const d = new Date(dateStr);
    return d.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center">
        <div className="text-center">
          <Shield className="w-12 h-12 text-primary mx-auto mb-4 animate-pulse" />
          <p className="text-slate-600">Chargement admin...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#FDFBF7] p-4 md:p-8">
        <div className="max-w-4xl mx-auto text-center pt-20">
          <Shield className="w-16 h-16 text-error mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-primary-900 mb-2">{error}</h1>
          <Button onClick={() => navigate('/dashboard')} variant="outline" className="mt-4">
            Retour au tableau de bord
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FDFBF7] p-4 md:p-8">
      <div className="max-w-7xl mx-auto">
        <button
          data-testid="back-to-dashboard"
          onClick={() => navigate('/dashboard')}
          className="flex items-center gap-2 text-slate-600 hover:text-primary mb-3 transition-colors text-sm"
        >
          <ArrowLeft className="w-4 h-4" />
          Retour au tableau de bord
        </button>

        <div className="flex items-center gap-3 mb-6 md:mb-8">
          <Shield className="w-7 h-7 md:w-8 md:h-8 text-primary" />
          <h1 data-testid="admin-title" className="text-2xl md:text-3xl font-bold text-primary-900 font-heading">
            Administration
          </h1>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 md:gap-4 mb-6 md:mb-8">
            <Card>
              <CardContent className="p-3 md:p-4 text-center">
                <Users className="w-5 h-5 text-primary mx-auto mb-1" />
                <p className="text-2xl font-bold text-primary-900">{stats.total_users}</p>
                <p className="text-xs text-slate-500">Inscrits</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-3 md:p-4 text-center">
                <UserPlus className="w-5 h-5 text-secondary mx-auto mb-1" />
                <p className="text-2xl font-bold text-primary-900">{stats.users_this_week}</p>
                <p className="text-xs text-slate-500">Cette semaine</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-3 md:p-4 text-center">
                <Calendar className="w-5 h-5 text-accent mx-auto mb-1" />
                <p className="text-2xl font-bold text-primary-900">{stats.users_this_month}</p>
                <p className="text-xs text-slate-500">Ce mois</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-3 md:p-4 text-center">
                <FileText className="w-5 h-5 text-secondary mx-auto mb-1" />
                <p className="text-2xl font-bold text-primary-900">{stats.total_notes}</p>
                <p className="text-xs text-slate-500">Notes</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-3 md:p-4 text-center">
                <CheckSquare className="w-5 h-5 text-accent mx-auto mb-1" />
                <p className="text-2xl font-bold text-primary-900">{stats.total_tasks}</p>
                <p className="text-xs text-slate-500">Tâches</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-3 md:p-4 text-center">
                <TrendingUp className="w-5 h-5 text-info mx-auto mb-1" />
                <p className="text-2xl font-bold text-primary-900">{stats.total_members}</p>
                <p className="text-xs text-slate-500">Bénéficiaires</p>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Users Table */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base md:text-lg flex items-center gap-2">
              <Users className="w-5 h-5 text-primary" />
              Utilisateurs inscrits ({users.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {/* Desktop Table */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full text-sm" data-testid="admin-users-table">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="text-left p-3 font-medium text-slate-600">Utilisateur</th>
                    <th className="text-left p-3 font-medium text-slate-600">Organisation</th>
                    <th className="text-left p-3 font-medium text-slate-600">Inscription</th>
                    <th className="text-left p-3 font-medium text-slate-600">Dernière connexion</th>
                    <th className="text-center p-3 font-medium text-slate-600">Connexions</th>
                    <th className="text-center p-3 font-medium text-slate-600">Activité</th>
                    <th className="text-center p-3 font-medium text-slate-600">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((u) => (
                    <tr key={u.id} className="border-b border-slate-100 hover:bg-slate-50/50" data-testid={`user-row-${u.id}`}>
                      <td className="p-3">
                        <div className="flex items-center gap-2">
                          {u.picture ? (
                            <img src={u.picture} alt="" className="w-8 h-8 rounded-full" />
                          ) : (
                            <div className="w-8 h-8 rounded-full bg-primary-50 flex items-center justify-center text-primary font-bold text-xs">
                              {u.full_name?.charAt(0)?.toUpperCase()}
                            </div>
                          )}
                          <div>
                            <p className="font-medium text-slate-900">
                              {u.full_name}
                              {u.is_admin && <span className="ml-2 px-1.5 py-0.5 bg-primary text-white text-xs rounded">Admin</span>}
                            </p>
                            <p className="text-xs text-slate-500">{u.email}</p>
                          </div>
                        </div>
                      </td>
                      <td className="p-3 text-slate-600">{u.organization}</td>
                      <td className="p-3 text-slate-600 text-xs">{formatDate(u.created_at)}</td>
                      <td className="p-3 text-slate-600 text-xs">{formatDate(u.last_login)}</td>
                      <td className="p-3 text-center font-medium">{u.login_count || 0}</td>
                      <td className="p-3">
                        <div className="flex items-center justify-center gap-3 text-xs text-slate-500">
                          <span title="Notes">{u.stats?.notes_count || 0} notes</span>
                          <span title="Tâches">{u.stats?.tasks_count || 0} tâches</span>
                        </div>
                      </td>
                      <td className="p-3 text-center">
                        {!u.is_admin && (
                          <button
                            data-testid={`delete-user-${u.id}`}
                            onClick={() => deleteUser(u.id, u.full_name)}
                            className="p-1.5 text-slate-400 hover:text-error hover:bg-error/10 rounded-lg transition-colors"
                            title="Supprimer"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile Cards */}
            <div className="md:hidden divide-y divide-slate-100">
              {users.map((u) => (
                <div key={u.id} className="p-4" data-testid={`user-card-${u.id}`}>
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center gap-2 min-w-0">
                      {u.picture ? (
                        <img src={u.picture} alt="" className="w-9 h-9 rounded-full flex-shrink-0" />
                      ) : (
                        <div className="w-9 h-9 rounded-full bg-primary-50 flex items-center justify-center text-primary font-bold text-sm flex-shrink-0">
                          {u.full_name?.charAt(0)?.toUpperCase()}
                        </div>
                      )}
                      <div className="min-w-0">
                        <p className="font-medium text-slate-900 text-sm truncate">
                          {u.full_name}
                          {u.is_admin && <span className="ml-1 px-1.5 py-0.5 bg-primary text-white text-xs rounded">Admin</span>}
                        </p>
                        <p className="text-xs text-slate-500 truncate">{u.email}</p>
                      </div>
                    </div>
                    {!u.is_admin && (
                      <button
                        onClick={() => deleteUser(u.id, u.full_name)}
                        className="p-1.5 text-slate-400 hover:text-error flex-shrink-0"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-xs text-slate-500 mt-2">
                    <div>Org : <span className="text-slate-700">{u.organization}</span></div>
                    <div>Connexions : <span className="text-slate-700 font-medium">{u.login_count || 0}</span></div>
                    <div>Inscrit : <span className="text-slate-700">{formatDate(u.created_at)}</span></div>
                    <div>Dernier : <span className="text-slate-700">{formatDate(u.last_login)}</span></div>
                  </div>
                </div>
              ))}
            </div>

            {users.length === 0 && (
              <div className="p-8 text-center text-slate-500">
                Aucun utilisateur inscrit
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AdminPage;
